import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate, Link } from 'react-router-dom';
import './AdminPanel.css';
import ConfirmModal from '../components/ConfirmModal';

// Recipe: Processing tile principle — an item not yet complete/failed
// must render as an explicit Processing state, never a blank/sourceless
// player. Threshold matches the Recipe's suggested default (several
// hours) for flagging a plausibly-stuck item so a reviewer isn't left
// guessing (1F).
const PROCESSING_STUCK_THRESHOLD_MS = 6 * 60 * 60 * 1000; // 6 hours

const ProcessingTile = ({ addedAt }) => {
    const elapsedMs = Date.now() - new Date(addedAt).getTime();
    const isStuck = elapsedMs >= PROCESSING_STUCK_THRESHOLD_MS;
    const hours = Math.floor(elapsedMs / (60 * 60 * 1000));
    const minutes = Math.floor((elapsedMs % (60 * 60 * 1000)) / (60 * 1000));

    return (
        <div className={`processing-tile${isStuck ? ' processing-tile-stuck' : ''}`}>
            <div className="processing-spinner"></div>
            <p className="processing-label">
                {isStuck
                    ? `Processing — Probably Bonking (${hours}h ${minutes}m)`
                    : 'Processing...'}
            </p>
        </div>
    );
};

const AdminPanel = () => {
    const navigate = useNavigate();
    const [allVisits, setAllVisits] = useState([]); // Store ALL visits for counts
    const [displayedVisits, setDisplayedVisits] = useState([]); // Filtered visits for display
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [error, setError] = useState(null);
    const [expandedVisit, setExpandedVisit] = useState(null);
    const [editingText, setEditingText] = useState({});
    const [savingVisit, setSavingVisit] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [modal, setModal] = useState(null); // { type: 'confirm' | 'error', message, onConfirm? } | null

    // Check admin authentication
    useEffect(() => {
        checkAdminAccess();
    }, []);

    const checkAdminAccess = async () => {
        try {
            const session = await fetchAuthSession();
            const groups = session.tokens?.accessToken?.payload['cognito:groups'] || [];
            
            if (groups.includes('admin')) {
                setIsAdmin(true);
                setCheckingAuth(false);
            } else {
                // Not admin - redirect to home
                navigate('/');
            }
        } catch (err) {
            // Not authenticated - redirect to home
            navigate('/');
        }
    };

    useEffect(() => {
        if (isAdmin) {
            loadAllVisits();
        }
    }, [isAdmin]);

    useEffect(() => {
        // Filter displayed visits when filter changes
        filterVisits();
    }, [filter, allVisits]);

    const closeModal = () => setModal(null);

    const showError = (message) => {
        setModal({ type: 'error', message });
    };

    const confirmAction = (message, onConfirm) => {
        setModal({ type: 'confirm', message, onConfirm });
    };

    const handleModalConfirm = () => {
        const action = modal?.onConfirm;
        setModal(null);
        if (action) action();
    };

    const loadAllVisits = async () => {
        // Only the very first load should show the full-page "Loading..."
        // and unmount everything else. A background refresh (after an
        // action like Approve/Reject) must NOT unmount the visible tree,
        // or the browser loses its scroll anchor and jumps to the top.
        if (!initialLoadComplete) {
            setLoading(true);
        }
        setError(null);

        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            // Load ALL visits (filter=all)
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/visits?filter=all`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to load visits');
            }

            const data = await response.json();
            setAllVisits(data.visits || []);
        } catch (err) {
            console.error('Error loading visits:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            setInitialLoadComplete(true);
        }
    };

    const filterVisits = () => {
        if (filter === 'all') {
            setDisplayedVisits(allVisits);
        } else {
            setDisplayedVisits(allVisits.filter(v => v.status === filter));
        }
    };

    const getCounts = () => {
        return {
            pending: allVisits.filter(v => v.status === 'pending').length,
            approved: allVisits.filter(v => v.status === 'approved').length,
            rejected: allVisits.filter(v => v.status === 'rejected').length
        };
    };

    const handleApproveAll = async (visitId) => {
        const visit = allVisits.find(v => v.visitId === visitId);
        if (visit && hasProcessingItems(visit)) {
            // Belt-and-suspenders: the button itself is disabled in this
            // state, but this guard covers any path that could still
            // reach here (e.g. a stale render).
            showError('This visit has an item still processing. Reject that item or wait for it to finish before approving the whole visit.');
            return;
        }

        setSavingVisit(visitId);
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            // First, approve all media items
            await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/visits/${visitId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        visitId,
                        updates: { approveAll: true }
                    })
                }
            );

            // Then, update status to approved
            await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/visits/${visitId}/status`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        visitId,
                        status: 'approved'
                    })
                }
            );

            // Reload visits
            await loadAllVisits();
        } catch (err) {
            console.error('Error approving visit:', err);
            showError('Failed to approve visit');
        } finally {
            setSavingVisit(null);
        }
    };

    const handlePublishApproved = (visitId) => {
        confirmAction(
            'Publish this visit with only the approved items? Rejected items will remain hidden.',
            () => doPublishApproved(visitId)
        );
    };

    const doPublishApproved = async (visitId) => {
        setSavingVisit(visitId);
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            // Just change status to approved (items already have their approval status)
            await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/visits/${visitId}/status`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        visitId,
                        status: 'approved'
                    })
                }
            );

            await loadAllVisits();
        } catch (err) {
            console.error('Error publishing visit:', err);
            showError('Failed to publish visit');
        } finally {
            setSavingVisit(null);
        }
    };

    const handleRejectAll = async (visitId) => {
        setSavingVisit(visitId);
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            // Reject all media items
            await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/visits/${visitId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        visitId,
                        updates: { rejectAll: true }
                    })
                }
            );

            // Update status to rejected
            await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/visits/${visitId}/status`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        visitId,
                        status: 'rejected'
                    })
                }
            );

            await loadAllVisits();
        } catch (err) {
            console.error('Error rejecting visit:', err);
            showError('Failed to reject visit');
        } finally {
            setSavingVisit(null);
        }
    };

    const handleMediaUpdate = async (visitId, mediaId, updates) => {
        setSavingVisit(visitId);
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            await fetch(
                `${process.env.REACT_APP_API_URL}/api/admin/visits/${visitId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        visitId,
                        updates: {
                            mediaUpdates: [{
                                mediaId,
                                ...updates
                            }]
                        }
                    })
                }
            );

            await loadAllVisits();
        } catch (err) {
            console.error('Error updating media:', err);
            showError('Failed to update media');
        } finally {
            setSavingVisit(null);
        }
    };

    const handleVisibilityChange = (visitId, mediaId, currentVisibility, newVisibility) => {
        // If changing family to public, show warning
        if (currentVisibility === 'family' && newVisibility === 'public') {
            confirmAction(
                '⚠️ WARNING: You are about to make family-only content PUBLIC.\n\nThis content was originally marked as private. Are you sure you want to make it visible to everyone?',
                () => handleMediaUpdate(visitId, mediaId, { visibility: newVisibility })
            );
            return;
        }

        handleMediaUpdate(visitId, mediaId, { visibility: newVisibility });
    };

    const handleTextEdit = (visitId, mediaId, newContent) => {
        setEditingText(prev => ({
            ...prev,
            [`${visitId}-${mediaId}`]: newContent
        }));
    };

    const saveTextEdit = async (visitId, mediaId) => {
        const key = `${visitId}-${mediaId}`;
        const newContent = editingText[key];
        
        if (newContent !== undefined) {
            await handleMediaUpdate(visitId, mediaId, { content: newContent });
            setEditingText(prev => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
        }
    };

    const toggleExpand = (visitId) => {
        setExpandedVisit(expandedVisit === visitId ? null : visitId);
    };

    // Helper: Check if item is approved (including legacy undefined)
    const isItemApproved = (item) => {
        return item.approved === true || item.approved === undefined;
    };

    // Helper: Check if visit has any approved items
    const hasApprovedItems = (visit) => {
        return Object.values(visit.media || {}).some(item => isItemApproved(item));
    };

    // Helper: Check if visit has any pending items
    const hasPendingItems = (visit) => {
        return Object.values(visit.media || {}).some(item => item.approved === null);
    };

    // Helper: Check if visit has any item still processing (Recipe:
    // Approve All & Publish must not mark a visit Approved while
    // something inside it isn't actually ready — 1F).
    const hasProcessingItems = (visit) => {
        return Object.values(visit.media || {}).some(item => item.uploadStatus === 'processing');
    };

    if (checkingAuth || loading) {
        return <div className="admin-panel loading">Loading...</div>;
    }

    if (error) {
        return (
            <div className="admin-panel error">
                <p>Error: {error}</p>
                <button onClick={loadAllVisits}>Retry</button>
            </div>
        );
    }

    const counts = getCounts();

    return (
        <div className="admin-panel">
            <header className="admin-header">
                <div className="admin-header-top">
                    <h1>Admin Panel</h1>
                    <Link to="/" className="admin-home-link">Home</Link>
                </div>
                <div className="admin-tabs">
                    <button
                        className={filter === 'pending' ? 'active' : ''}
                        onClick={() => setFilter('pending')}
                    >
                        Pending ({counts.pending})
                    </button>
                    <button
                        className={filter === 'approved' ? 'active' : ''}
                        onClick={() => setFilter('approved')}
                    >
                        Approved ({counts.approved})
                    </button>
                    <button
                        className={filter === 'rejected' ? 'active' : ''}
                        onClick={() => setFilter('rejected')}
                    >
                        Rejected ({counts.rejected})
                    </button>
                </div>
            </header>

            <div className="admin-content">
                {displayedVisits.length === 0 ? (
                    <div className="no-visits">
                        <p>No {filter} visits</p>
                    </div>
                ) : (
                    displayedVisits.map(visit => {
                        const isExpanded = expandedVisit === visit.visitId;
                        const isSaving = savingVisit === visit.visitId;
                        const showPublishButton = visit.status === 'pending' && hasApprovedItems(visit);

                        return (
                            <div key={visit.visitId} className="visit-card">
                                <div className="visit-header" onClick={() => toggleExpand(visit.visitId)}>
                                    <div className="visit-info">
                                        <h3>{visit.discId} - {visit.courseName || visit.location?.courseName}</h3>
                                        <p className="visit-date">
                                            {new Date(visit.timestamp).toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                        <p className="visit-status">Status: {visit.status}</p>
                                    </div>
                                    <div className="expand-icon">
                                        {isExpanded ? '▼' : '▶'}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="visit-details">
                                        <div className="media-items">
                                            {Object.entries(visit.media || {}).map(([mediaId, item]) => {
                                                const editKey = `${visit.visitId}-${mediaId}`;
                                                const isEditing = editingText[editKey] !== undefined;
                                                const currentContent = isEditing ? editingText[editKey] : item.content;
                                                const itemApproved = isItemApproved(item);

                                                return (
                                                    <div key={mediaId} className="media-item">
                                                        <div className="media-item-header">
                                                            <span className="media-type">{item.type.toUpperCase()}</span>
                                                            <span className={`visibility-badge ${item.visibility}`}>
                                                                {item.visibility === 'family' ? '👨‍👩‍👧 Family Only' : '🌍 Public'}
                                                            </span>
                                                            <span className={`approval-status ${itemApproved ? 'approved' : item.approved === false ? 'rejected' : 'pending'}`}>
                                                                {itemApproved ? '✓ Approved' : item.approved === false ? '✗ Rejected' : '⏳ Pending'}
                                                            </span>
                                                        </div>

                                                        <div className="media-content">
                                                            {item.uploadStatus === 'processing' ? (
                                                                <ProcessingTile addedAt={item.addedAt} />
                                                            ) : (
                                                                <>
                                                                    {item.type === 'photo' && (
                                                                        <img src={item.url} alt="Visit photo" className="media-photo" />
                                                                    )}

                                                                    {item.type === 'video' && (
                                                                        <video src={item.url} controls className="media-video" />
                                                                    )}

                                                                    {item.type === 'audio' && (
                                                                        <audio src={item.url} controls className="media-audio" />
                                                                    )}
                                                                </>
                                                            )}

                                                            {item.type === 'text' && (
                                                                <div className="media-text">
                                                                    {isEditing ? (
                                                                        <textarea
                                                                            value={currentContent}
                                                                            onChange={(e) => handleTextEdit(visit.visitId, mediaId, e.target.value)}
                                                                            rows={4}
                                                                        />
                                                                    ) : (
                                                                        <p>{item.content}</p>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="media-actions">
                                                            {item.type === 'text' && (
                                                                isEditing ? (
                                                                    <>
                                                                        <button
                                                                            className="btn-save"
                                                                            onClick={() => saveTextEdit(visit.visitId, mediaId)}
                                                                            disabled={isSaving}
                                                                        >
                                                                            Save
                                                                        </button>
                                                                        <button
                                                                            className="btn-cancel"
                                                                            onClick={() => {
                                                                                setEditingText(prev => {
                                                                                    const updated = { ...prev };
                                                                                    delete updated[editKey];
                                                                                    return updated;
                                                                                });
                                                                            }}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <button
                                                                        className="btn-edit"
                                                                        onClick={() => handleTextEdit(visit.visitId, mediaId, item.content)}
                                                                    >
                                                                        ✏️ Edit
                                                                    </button>
                                                                )
                                                            )}

                                                            {/* Visibility change buttons with warning */}
                                                            {item.visibility === 'public' ? (
                                                                <button
                                                                    className="btn-change-visibility"
                                                                    onClick={() => handleVisibilityChange(visit.visitId, mediaId, 'public', 'family')}
                                                                    disabled={isSaving}
                                                                >
                                                                    Change to Family Only
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    className="btn-change-visibility-warning"
                                                                    onClick={() => handleVisibilityChange(visit.visitId, mediaId, 'family', 'public')}
                                                                    disabled={isSaving}
                                                                >
                                                                    ⚠️ Change to Public
                                                                </button>
                                                            )}

                                                            {!itemApproved && (
                                                                <button
                                                                    className="btn-approve-item"
                                                                    onClick={() => handleMediaUpdate(visit.visitId, mediaId, { approved: true })}
                                                                    disabled={isSaving || item.uploadStatus === 'processing'}
                                                                    title={item.uploadStatus === 'processing' ? 'Cannot approve until processing completes' : undefined}
                                                                >
                                                                    ✓ Approve
                                                                </button>
                                                            )}

                                                            {item.approved !== false && (
                                                                <button
                                                                    className="btn-reject-item"
                                                                    onClick={() => handleMediaUpdate(visit.visitId, mediaId, { approved: false })}
                                                                    disabled={isSaving}
                                                                >
                                                                    ✗ Reject
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="visit-actions">
                                            {visit.status === 'pending' && (
                                                <>
                                                    <button
                                                        className="btn-approve-all"
                                                        onClick={() => handleApproveAll(visit.visitId)}
                                                        disabled={isSaving || hasProcessingItems(visit)}
                                                        title={hasProcessingItems(visit) ? 'Cannot approve the whole visit while an item is still processing' : undefined}
                                                    >
                                                        {isSaving ? 'Saving...' : '✓ Approve All & Publish'}
                                                    </button>
                                                    
                                                    {showPublishButton && (
                                                        <button
                                                            className="btn-publish-approved"
                                                            onClick={() => handlePublishApproved(visit.visitId)}
                                                            disabled={isSaving}
                                                        >
                                                            {isSaving ? 'Saving...' : '📤 Publish Approved Items Only'}
                                                        </button>
                                                    )}
                                                    
                                                    <button
                                                        className="btn-reject-all"
                                                        onClick={() => handleRejectAll(visit.visitId)}
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? 'Saving...' : '✗ Reject All'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <ConfirmModal
                isOpen={!!modal}
                type={modal?.type}
                message={modal?.message}
                onConfirm={handleModalConfirm}
                onClose={closeModal}
            />
        </div>
    );
};

export default AdminPanel;